import type { QueryCondition } from '@edulead/models';
import { create } from 'zustand';
import { DEFAULT_BEACON_FILL } from '../components/navigator/beacon-colors';
import type { MapBounds } from '../components/navigator/map-focus';
import { applyBeaconIds, BeaconIdsVerb } from '../components/navigator/beacon-ids';
import { toastV4Error } from '../services/edulead-v4';
import { eduleadV4 } from '../services/edulead-v4-client';
import type { Edulead } from 'edulead';

/**
 * How districts are picked on the map while a beacon is open. Pointer: plain drag pans, a click
 * toggles one district, Shift+drag paints. Brush: plain drag paints. Rectangle and oval: plain
 * drag draws the shape, and every district it touches takes the stroke's verb.
 */
export type BeaconTool = 'pointer' | 'brush' | 'rectangle' | 'oval';

interface NavigatorState {
  // The institutions of the selected beacons, or of the query's beacons with a radius, which the
  // map highlights; empty for none
  highlightedInstitutionIds: string[];
  setHighlightedInstitutionIds: (ids: string[]) => void;
  // The fill of each highlighted institution (by id), as Mapbox takes it: its beacon's color,
  // the first selected beacon's where they share one. An institution without one takes
  // beaconFill.
  highlightFills: Record<string, string>;
  setHighlightFills: (fills: Record<string, string>) => void;
  // The fill of the beacon in hand: the edited one's, or the searched beacons' first; what a
  // stroke lights, the hover, the marquee and the radius take it. DEFAULT_BEACON_FILL otherwise.
  beaconFill: string;
  setBeaconFill: (fill: string) => void;
  // The beacon the map edits: the one beacon selected in Beacons mode, for those who may change
  // it; its districts are added and removed on click, with the tools and the right-click menu.
  // Null while none is. The highlighted ids are its members as edited so far.
  editingBeaconId: string | null;
  setEditingBeaconId: (id: string | null) => void;
  // The selected beacon's members as the API holds them; null while not one beacon is selected or
  // before they are read. Edits on the map are the difference between these and the highlighted
  // ids, staged until saved or discarded from the panel.
  beaconSavedIds: string[] | null;
  setBeaconSavedIds: (ids: string[] | null) => void;
  isSavingBeacon: boolean;
  // The map's selection tool for the edited beacon; kept across beacons for the session.
  beaconTool: BeaconTool;
  setBeaconTool: (tool: BeaconTool) => void;
  // The names of the institutions seen so far, by id: the beacon members the API listed and the
  // districts painted on the map, so the panel can list a beacon's members by name.
  institutionNames: Record<string, string>;
  rememberInstitutionNames: (names: Record<string, string>) => void;
  // Adds or removes a stroke's institutions in the edited set; nothing is sent until saved. The
  // names, when given, are remembered.
  paintBeacon: (ids: string[], verb: BeaconIdsVerb, names?: Record<string, string>) => void;
  // Sends the edits: one batch call for the added ids, one for the removed. A call that fails
  // leaves what it would have changed unsaved, to retry or discard, and toasts why.
  saveBeaconEdits: () => Promise<void>;
  // The edited sets before each gesture since the beacon was opened, saved or discarded, and
  // those undone: one gesture (a click, a stroke, a marquee, a row button) is one step.
  beaconUndoStack: string[][];
  beaconRedoStack: string[][];
  undoBeaconEdit: () => void;
  redoBeaconEdit: () => void;
  // Territories mode. The color of each state (its two-letter code) the selected territories
  // cover, the first territory's where they overlap, which the map fills; empty for none.
  territoryFills: Record<string, string>;
  setTerritoryFills: (fills: Record<string, string>) => void;
  // The territory the map edits, the one selected alone: a click on a state adds or removes it.
  // Null while none is. Its states as edited so far, and as the API holds them (null with no
  // territory edited); edits are staged until saved.
  editingTerritoryId: string | null;
  territoryStates: string[];
  territorySavedStates: string[] | null;
  // Starts editing a territory from what the API holds, or stops (null), dropping any edits.
  editTerritory: (id: string | null, states: string[] | null) => void;
  // Adds the states to the edited territory or removes them: one undo step.
  paintTerritory: (states: string[], verb: BeaconIdsVerb) => void;
  territoryUndoStack: string[][];
  territoryRedoStack: string[][];
  undoTerritoryEdit: () => void;
  redoTerritoryEdit: () => void;
  isSavingTerritory: boolean;
  // Sends the edited states (update_mask states); on success they are what the API holds.
  saveTerritoryEdits: () => Promise<void>;

  // The radius in miles drawn around the highlighted institutions (the query's Expansion); 0 for
  // none
  highlightedRadiusMiles: number;
  setHighlightedRadiusMiles: (miles: number) => void;

  // The query's last count: matching people per account (district or school) id, and the
  // totals; null before a search
  headcounts: Record<string, number> | null;
  headcountTotals: { people: number; accounts: number } | null;
  // The Navigator panel's query the counts are of, as the people search's conditions, so a
  // clicked district can list its people; and the URL's ?q= they came from, so the same query
  // is not counted again. Null with the counts.
  headcountsConditions: QueryCondition[] | null;
  headcountsQueryKey: string | null;
  isCountingHeadcounts: boolean;
  setIsCountingHeadcounts: (isCounting: boolean) => void;
  /** Whether the People or Institutions view is loading its list, for the panel's Search button. */
  isLoadingList: boolean;
  setIsLoadingList: (isLoading: boolean) => void;
  setQueryHeadcounts: (
    queryKey: string,
    conditions: QueryCondition[],
    counts: Record<string, number>,
    totals: { people: number; accounts: number },
    focus: MapBounds | null,
  ) => void;
  clearHeadcounts: () => void;
  /**
   * Where the Navigator query's people are, as a box the map fits itself to once the counts are
   * in (null with the counts, and when no account has a location), and the
   * query (its key) the map last fitted to, so coming back to the Map view does not refit.
   */
  headcountsFocus: MapBounds | null;
  focusedQueryKey: string | null;
  setFocusedQueryKey: (queryKey: string) => void;
  /**
   * Where the map was last left (its center and zoom), so coming back to the Map view from a
   * list shows the same place; null before the map first moves.
   */
  mapViewState: MapViewState | null;
  setMapViewState: (viewState: MapViewState) => void;
  /**
   * Each Navigator mode's query string as last left (its ?q=, page, open beacon …), so switching
   * back to a mode restores it.
   */
  modeSearches: Record<string, string>;
  setModeSearch: (mode: string, search: string) => void;
}

/** A map viewport: its center and zoom, as react-map-gl's initialViewState takes them. */
export interface MapViewState {
  longitude: number;
  latitude: number;
  zoom: number;
}

export const useNavigatorStore = create<NavigatorState>((set, get) => ({
  highlightedInstitutionIds: [],
  setHighlightedInstitutionIds: (ids: string[]) => set({ highlightedInstitutionIds: ids }),
  highlightFills: {},
  setHighlightFills: (fills) => set({ highlightFills: fills }),
  beaconFill: DEFAULT_BEACON_FILL,
  setBeaconFill: (fill) => set({ beaconFill: fill }),
  editingBeaconId: null,
  setEditingBeaconId: (id: string | null) => set({ editingBeaconId: id }),
  beaconSavedIds: null,
  // A new saved set starts a new history: the edits before it are gone or committed.
  setBeaconSavedIds: (ids: string[] | null) =>
    set({ beaconSavedIds: ids, beaconUndoStack: [], beaconRedoStack: [] }),
  isSavingBeacon: false,
  beaconTool: 'pointer',
  setBeaconTool: (tool) => set({ beaconTool: tool }),
  institutionNames: {},
  rememberInstitutionNames: (names) =>
    set({ institutionNames: { ...get().institutionNames, ...names } }),
  paintBeacon: (ids: string[], verb: BeaconIdsVerb, names?: Record<string, string>) => {
    // Nothing moves while a save is in flight, so the save's diff stays what it was computed from.
    if (!get().editingBeaconId || get().isSavingBeacon || ids.length === 0) return;
    const current = get().highlightedInstitutionIds;
    const wanted = new Set(ids);
    const present = new Set(current);
    const next =
      verb === 'Added'
        ? [...current, ...ids.filter((id) => !present.has(id))]
        : current.filter((id) => !wanted.has(id));
    // A gesture that changes nothing (removing what is out, adding what is in) is no step.
    if (next.length === current.length) {
      if (names) set({ institutionNames: { ...get().institutionNames, ...names } });
      return;
    }
    set({
      highlightedInstitutionIds: next,
      beaconUndoStack: [...get().beaconUndoStack, current],
      beaconRedoStack: [],
      ...(names ? { institutionNames: { ...get().institutionNames, ...names } } : {}),
    });
  },
  beaconUndoStack: [],
  beaconRedoStack: [],
  undoBeaconEdit: () => {
    const { beaconUndoStack, beaconRedoStack, highlightedInstitutionIds, isSavingBeacon } = get();
    if (isSavingBeacon || beaconUndoStack.length === 0) return;
    set({
      highlightedInstitutionIds: beaconUndoStack[beaconUndoStack.length - 1],
      beaconUndoStack: beaconUndoStack.slice(0, -1),
      beaconRedoStack: [...beaconRedoStack, highlightedInstitutionIds],
    });
  },
  redoBeaconEdit: () => {
    const { beaconUndoStack, beaconRedoStack, highlightedInstitutionIds, isSavingBeacon } = get();
    if (isSavingBeacon || beaconRedoStack.length === 0) return;
    set({
      highlightedInstitutionIds: beaconRedoStack[beaconRedoStack.length - 1],
      beaconRedoStack: beaconRedoStack.slice(0, -1),
      beaconUndoStack: [...beaconUndoStack, highlightedInstitutionIds],
    });
  },
  saveBeaconEdits: async () => {
    const {
      editingBeaconId: beaconId,
      beaconSavedIds: saved,
      highlightedInstitutionIds: edited,
    } = get();
    if (!beaconId || !saved || get().isSavingBeacon) return;
    const savedSet = new Set(saved);
    const editedSet = new Set(edited);
    const added = edited.filter((id) => !savedSet.has(id));
    const removed = saved.filter((id) => !editedSet.has(id));
    if (added.length === 0 && removed.length === 0) return;
    // What is saved cannot be undone here; Discard and a new stroke take over.
    set({ isSavingBeacon: true, beaconUndoStack: [], beaconRedoStack: [] });
    try {
      if (added.length > 0) {
        await applyBeaconIds(beaconId, added, 'Added');
        // The adds hold even if the removes fail: the saved set says so.
        set({ beaconSavedIds: [...(get().beaconSavedIds ?? []), ...added] });
      }
      if (removed.length > 0) {
        await applyBeaconIds(beaconId, removed, 'Removed');
        const gone = new Set(removed);
        set({ beaconSavedIds: (get().beaconSavedIds ?? []).filter((id) => !gone.has(id)) });
      }
    } catch (error) {
      toastV4Error(error, 'Failed to save the beacon');
    } finally {
      set({ isSavingBeacon: false });
    }
  },
  territoryFills: {},
  setTerritoryFills: (fills) => set({ territoryFills: fills }),
  editingTerritoryId: null,
  territoryStates: [],
  territorySavedStates: null,
  editTerritory: (id, states) =>
    set({
      editingTerritoryId: id,
      territoryStates: id ? (states ?? []) : [],
      territorySavedStates: id ? (states ?? []) : null,
      territoryUndoStack: [],
      territoryRedoStack: [],
    }),
  paintTerritory: (states, verb) => {
    const { editingTerritoryId, isSavingTerritory, territoryStates: current } = get();
    if (!editingTerritoryId || isSavingTerritory || states.length === 0) return;
    const present = new Set(current);
    const wanted = new Set(states);
    const next =
      verb === 'Added'
        ? [...current, ...states.filter((s) => !present.has(s))]
        : current.filter((s) => !wanted.has(s));
    if (next.length === current.length) return;
    set({
      territoryStates: next,
      territoryUndoStack: [...get().territoryUndoStack, current],
      territoryRedoStack: [],
    });
  },
  territoryUndoStack: [],
  territoryRedoStack: [],
  undoTerritoryEdit: () => {
    const { territoryUndoStack, territoryRedoStack, territoryStates, isSavingTerritory } = get();
    if (isSavingTerritory || territoryUndoStack.length === 0) return;
    set({
      territoryStates: territoryUndoStack[territoryUndoStack.length - 1],
      territoryUndoStack: territoryUndoStack.slice(0, -1),
      territoryRedoStack: [...territoryRedoStack, territoryStates],
    });
  },
  redoTerritoryEdit: () => {
    const { territoryUndoStack, territoryRedoStack, territoryStates, isSavingTerritory } = get();
    if (isSavingTerritory || territoryRedoStack.length === 0) return;
    set({
      territoryStates: territoryRedoStack[territoryRedoStack.length - 1],
      territoryRedoStack: territoryRedoStack.slice(0, -1),
      territoryUndoStack: [...territoryUndoStack, territoryStates],
    });
  },
  isSavingTerritory: false,
  saveTerritoryEdits: async () => {
    const { editingTerritoryId: id, territoryStates: states } = get();
    if (!id || get().isSavingTerritory || !hasTerritoryEdits(get())) return;
    // What is saved cannot be undone here.
    set({ isSavingTerritory: true, territoryUndoStack: [], territoryRedoStack: [] });
    try {
      const { territory } = await eduleadV4.territories.update({
        id,
        update_mask: 'states',
        body: { states: states as Edulead.EduleadV4Territory.States.Item[] },
      });
      if (get().editingTerritoryId === id) {
        set({ territorySavedStates: territory?.states ?? states });
      }
    } catch (error) {
      toastV4Error(error, 'Failed to save the territory');
    } finally {
      set({ isSavingTerritory: false });
    }
  },
  highlightedRadiusMiles: 0,
  setHighlightedRadiusMiles: (miles: number) => set({ highlightedRadiusMiles: miles }),
  headcounts: null,
  headcountTotals: null,
  headcountsConditions: null,
  headcountsQueryKey: null,
  isCountingHeadcounts: false,
  setIsCountingHeadcounts: (isCounting) => set({ isCountingHeadcounts: isCounting }),
  isLoadingList: false,
  setIsLoadingList: (isLoading) => set({ isLoadingList: isLoading }),
  setQueryHeadcounts: (queryKey, conditions, counts, totals, focus) =>
    set({
      headcounts: counts,
      headcountTotals: totals,
      headcountsConditions: conditions,
      headcountsQueryKey: queryKey,
      headcountsFocus: focus,
    }),
  clearHeadcounts: () =>
    set({
      headcounts: null,
      headcountTotals: null,
      headcountsConditions: null,
      headcountsQueryKey: null,
      headcountsFocus: null,
    }),
  headcountsFocus: null,
  focusedQueryKey: null,
  setFocusedQueryKey: (queryKey) => set({ focusedQueryKey: queryKey }),
  mapViewState: null,
  setMapViewState: (viewState) => set({ mapViewState: viewState }),
  modeSearches: {},
  setModeSearch: (mode, search) =>
    set((state) => ({ modeSearches: { ...state.modeSearches, [mode]: search } })),
}));

/** Whether the edited beacon's members, as the map shows them, differ from what the API holds. */
export function hasBeaconEdits(state: {
  beaconSavedIds: string[] | null;
  highlightedInstitutionIds: string[];
}): boolean {
  const { beaconSavedIds: saved, highlightedInstitutionIds: edited } = state;
  if (!saved) return false;
  const savedSet = new Set(saved);
  const editedSet = new Set(edited);
  return edited.some((id) => !savedSet.has(id)) || saved.some((id) => !editedSet.has(id));
}

/** Whether the edited territory's states differ from what the API holds. */
export function hasTerritoryEdits(state: {
  territorySavedStates: string[] | null;
  territoryStates: string[];
}): boolean {
  const { territorySavedStates: saved, territoryStates: edited } = state;
  if (!saved) return false;
  const savedSet = new Set(saved);
  const editedSet = new Set(edited);
  return edited.some((s) => !savedSet.has(s)) || saved.some((s) => !editedSet.has(s));
}
